// ContactPage Component (Standalone Contact Page)
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { HiPaperAirplane, HiArrowLeft } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer'; 

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage('');

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setStatusMessage('Thank you for your message! We\'ll get back to you soon.');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatusMessage(result.message || 'An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setStatusMessage('Could not connect to the server. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 relative">
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated background shapes */}
      </div>

      <div className="relative z-10 pt-8 px-4">
        <Link to="/">
          <motion.button whileHover={{ scale: 1.05, x: -5 }} whileTap={{ scale: 0.95 }} className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors duration-300">
            <HiArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </motion.button>
        </Link>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen py-12">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="w-full max-w-2xl mx-auto px-4">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-neutral-900 mb-4">Contact <span className="text-primary-500">Us</span></h1>
            <p className="text-xl text-neutral-700">We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Form Inputs */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">Your Name</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-4 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 text-lg" placeholder="John Doe" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">Email Address</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-4 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 text-lg" placeholder="john@example.com" />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-neutral-700 mb-2">Subject</label>
                <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required className="w-full px-4 py-4 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 text-lg" placeholder="Project Discussion" />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-2">Message</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={6} className="w-full px-4 py-4 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 resize-none text-lg" placeholder="Tell us about your project or how we can help you..."></textarea>
              </div>
              
              {statusMessage && (
                <p className="text-center text-neutral-600">{statusMessage}</p>
              )}

              <motion.button type="submit" disabled={isSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className={`w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center justify-center space-x-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}>
                {isSubmitting ? (<div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />) : (<><span>Send Message</span><HiPaperAirplane className="w-5 h-5" /></>)}
              </motion.button>
              
              {/* Added note about alternative contact method */}
              <p className="text-center text-neutral-600 mt-4">
                Alternatively, you can email us directly at <a href="mailto:hr@efatechsolutions.com" className="text-primary-500 hover:underline">hr@efatechsolutions.com</a>
              </p>
            </form>
          </motion.div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;
